import {
  endConversationSession,
  endConversationSessionOnUnload,
  sendHeartbeat,
} from "./api";
import { match } from "ts-pattern";

interface SessionConfig {
  apiUrl: string;
  heartbeatInterval?: number; // milliseconds, default 30000
}

interface SessionData {
  sessionId: string;
  startTime: number;
  lastHeartbeat: number | null;
}

type SessionPhase =
  | { type: "inactive" }
  | {
      type: "active";
      sessionId: string;
      startTime: number;
      lastHeartbeat: number | null;
    }
  | { type: "ending"; sessionId: string }
  | { type: "end_failed"; sessionId: string }
  | { type: "ended" }
  | { type: "destroyed" };

class SessionService {
  private config: { apiUrl: string; heartbeatInterval: number };
  private phase: SessionPhase = { type: "inactive" };
  private heartbeatTimer: number | null = null;
  private endSessionPromise: Promise<void> | null = null;
  private debug: boolean = false;

  constructor(config: SessionConfig, debug: boolean = false) {
    this.config = {
      heartbeatInterval: 30000, // 30 seconds default
      ...config,
    };
    this.debug = debug;
  }

  canCreateSession(): boolean {
    return match(this.phase)
      .with({ type: "inactive" }, () => true)
      .with({ type: "ended" }, () => true)
      .otherwise(() => false);
  }

  createSession(sessionId: string): boolean {
    if (!this.canCreateSession()) {
      this.log("Cannot create session in current phase:", this.phase.type);
      return false;
    }

    this.phase = {
      type: "active",
      sessionId,
      startTime: Date.now(),
      lastHeartbeat: null,
    };

    this.log("Session created:", sessionId);
    this.startHeartbeat();
    return true;
  }

  async endSession(): Promise<void> {
    if (this.phase.type === "ending") {
      if (this.endSessionPromise) await this.endSessionPromise;
      return;
    }
    if (this.phase.type !== "active" && this.phase.type !== "end_failed") {
      return;
    }
    const { sessionId } = this.phase;

    this.stopHeartbeat();
    this.phase = { type: "ending", sessionId };
    const pendingEnd = endConversationSession(this.config.apiUrl, sessionId);
    this.endSessionPromise = pendingEnd;

    try {
      await pendingEnd;
      this.log("Session ended successfully:", sessionId);
      if (!this.isDestroyed()) this.phase = { type: "ended" };
    } catch (error) {
      this.log("Error ending session:", error);
      if (!this.isDestroyed()) this.phase = { type: "end_failed", sessionId };
      throw error;
    } finally {
      if (this.endSessionPromise === pendingEnd) this.endSessionPromise = null;
    }
  }

  private startHeartbeat(): void {
    if (this.phase.type !== "active") return;

    // Send immediate heartbeat
    this.sendHeartbeatNow();

    // Set up interval
    this.heartbeatTimer = window.setInterval(() => {
      this.sendHeartbeatNow();
    }, this.config.heartbeatInterval);

    this.log("Heartbeat started");
  }

  private stopHeartbeat(): void {
    if (this.heartbeatTimer) {
      window.clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
      this.log("Heartbeat stopped");
    }
  }

  private async sendHeartbeatNow(): Promise<void> {
    if (this.phase.type !== "active") return;

    try {
      await sendHeartbeat(this.config.apiUrl, this.phase.sessionId);
      if (this.phase.type === "active") {
        // State might have changed
        this.phase.lastHeartbeat = Date.now();
      }
      this.log("Heartbeat sent");
    } catch (error) {
      this.log("Heartbeat failed:", error);
      // Don't throw - allow heartbeat failures without breaking session
    }
  }

  getCurrentSessionId(): string | null {
    return this.phase.type === "active" ||
      this.phase.type === "ending" ||
      this.phase.type === "end_failed"
      ? this.phase.sessionId
      : null;
  }

  getSessionDuration(): number {
    return match(this.phase)
      .with({ type: "active" }, ({ startTime }) => Date.now() - startTime)
      .otherwise(() => 0);
  }

  isSessionActive(): boolean {
    return this.phase.type === "active";
  }

  private log(...args: unknown[]): void {
    if (this.debug) {
      console.log("[SessionService]", ...args);
    }
  }

  private isDestroyed(): boolean {
    return this.phase.type === "destroyed";
  }

  mount(): void {
    if (this.phase.type === "destroyed") this.phase = { type: "inactive" };
  }

  destroy(): void {
    this.stopHeartbeat();
    if (
      this.phase.type === "active" ||
      this.phase.type === "ending" ||
      this.phase.type === "end_failed"
    ) {
      const { sessionId } = this.phase;
      try {
        endConversationSessionOnUnload(this.config.apiUrl, sessionId);
      } catch (error) {
        console.error("[SessionService] Unmount session cleanup failed:", error);
      }
    }
    this.phase = { type: "destroyed" };
  }
}

export default SessionService;
export type { SessionConfig, SessionData };
